import { normalizeFilePathForComparison } from '@lved/shared-core';
import { Disposable } from '@theia/core/lib/common/disposable';
import { injectable } from '@theia/core/shared/inversify';

// Prevents bulk workspace initialization writes from reaching live-edit listeners.
// Keep this service dependency-neutral: both the platform bridge and Online Preview loader use it,
// and importing either contribution here would recreate a circular frontend initialization path.
@injectable()
export class FileChangeSuppressionService {
    protected readonly roots: Set<string> = new Set<string>();
    protected readonly refCounts: Map<string, number> = new Map<string, number>();

    // Suppress file-change handling under this root until every returned handle for the root is disposed.
    suppressUnder(rootPath: string): Disposable {
        const normalizedRootPath: string = normalizeFilePathForComparison(rootPath);
        const currentCount: number = this.refCounts.get(normalizedRootPath) ?? 0;
        this.refCounts.set(normalizedRootPath, currentCount + 1);
        this.roots.add(normalizedRootPath);
        return Disposable.create((): void => {
            const count: number = this.refCounts.get(normalizedRootPath) ?? 0;
            if (count <= 1) {
                this.refCounts.delete(normalizedRootPath);
                this.roots.delete(normalizedRootPath);
            } else {
                this.refCounts.set(normalizedRootPath, count - 1);
            }
        });
    }

    isSuppressed(path: string): boolean {
        const normalizedPath: string = normalizeFilePathForComparison(path);
        return Array.from(this.roots).some(
            (rootPath: string): boolean => normalizedPath === rootPath || normalizedPath.startsWith(`${rootPath}/`)
        );
    }
}
